import { initializePaddle, type Paddle } from '@paddle/paddle-js';
import type { CheckoutParamsResponseCustomData } from 'we-tree-api-client/v2';
import i18n from '@/i18n/i18n';
import { markBillingActivating } from '@/utils/billingActivation';

function paddleNotConfiguredError(): Error {
  return new Error(i18n.t('forms:oneTimePayment.errors.paddleNotConfigured'));
}

let paddlePromise: Promise<Paddle | undefined> | null = null;

type CheckoutListener = {
  onCompleted?: () => void;
  onClosed?: () => void;
  familyId?: string;
};

let activeListener: CheckoutListener | null = null;

function paddleClientToken(): string | undefined {
  return import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
}

function paddleEnvironment(): 'sandbox' | 'production' {
  return import.meta.env.VITE_PADDLE_ENV === 'production' ? 'production' : 'sandbox';
}

/** Lazy-init Paddle.js for overlay checkout (sandbox by default). */
export function getPaddleInstance(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    const token = paddleClientToken();
    if (!token) {
      return Promise.resolve(undefined);
    }
    paddlePromise = initializePaddle({
      token,
      environment: paddleEnvironment(),
      eventCallback: (event) => {
        if (event.name === 'checkout.completed') {
          if (activeListener?.familyId) {
            markBillingActivating(activeListener.familyId);
          }
          activeListener?.onCompleted?.();
        }
        if (event.name === 'checkout.closed') {
          activeListener?.onClosed?.();
        }
      }
    });
  }
  return paddlePromise;
}

export function isPaddleConfigured(): boolean {
  return Boolean(paddleClientToken());
}

export type OpenCheckoutArgs = {
  familyId: string;
  priceId: string;
  customerId: string;
  customData: CheckoutParamsResponseCustomData;
  /** Absolute URL Paddle uses for “Return to …” after success */
  successUrl: string;
  onCompleted?: () => void;
  onClosed?: () => void;
};

/** Open Paddle.js overlay checkout for a catalog price (subscription upgrade). */
export async function openPaddleCheckout(args: OpenCheckoutArgs): Promise<void> {
  const paddle = await getPaddleInstance();
  if (!paddle) {
    throw paddleNotConfiguredError();
  }

  activeListener = {
    familyId: args.familyId,
    onCompleted: args.onCompleted,
    onClosed: args.onClosed
  };

  paddle.Checkout.open({
    items: [{ priceId: args.priceId, quantity: 1 }],
    customer: { id: args.customerId },
    customData: {
      family_id: args.customData.family_id,
      user_id: args.customData.user_id
    },
    settings: {
      variant: 'one-page',
      displayMode: 'overlay',
      successUrl: args.successUrl
    }
  });
}

export type OpenOneTimeCheckoutArgs = {
  transactionId: string;
  successUrl: string;
  /** Optional — only mark upgrade-activating when set (subscription flow). */
  familyId?: string;
  onCompleted?: () => void;
  onClosed?: () => void;
};

/**
 * Open Paddle.js overlay for a server-prepared one-time transaction
 * (contribute / donate with custom amounts).
 */
export async function openPaddleOneTimeCheckout(args: OpenOneTimeCheckoutArgs): Promise<void> {
  const paddle = await getPaddleInstance();
  if (!paddle) {
    throw paddleNotConfiguredError();
  }

  activeListener = {
    familyId: args.familyId,
    onCompleted: args.onCompleted,
    onClosed: args.onClosed
  };

  paddle.Checkout.open({
    transactionId: args.transactionId,
    settings: {
      variant: 'one-page',
      displayMode: 'overlay',
      successUrl: args.successUrl
    }
  });
}
